import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import {
  Envelope, Plus, DotsThree,
  Trash, CheckCircle, Star, Globe,
  PencilSimple, ArrowsClockwise, Clock, WarningCircle, ShieldCheck,
} from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { usePermission } from '@/hooks/usePermission'
import { cn, getBadgeClasses, getBadgeDotColor, getBadgeIconTint, badgeVariantStyles } from '@/lib/utils'
import { senderEmailsApi } from '@/lib/api'

import AddSenderEmailModal from './AddSenderEmailModal'
import EditSenderEmailModal from './EditSenderEmailModal'
import DnsStatusBadge from '@/pages/settings/components/DnsStatusBadge'
import WarmupSection from '@/components/warmup/WarmupSection'
import { isFullyVerified } from '@/lib/senderVerification'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { ReadOnlyBanner, SectionLocked } from '@/pages/settings/components/SectionAccess'
import { DotLoader } from '@/components/ui/DotLoader'
import type { SenderEmail } from '@/types/senderEmail'

/**
 * Labels only. The *colour* of a sender state is decided in one place —
 * `statusToBadge` in `lib/utils` (§60) — and read through `getBadgeClasses` /
 * `getBadgeIconTint`. This map used to carry its own yellow/blue/green chips,
 * none of them on the palette and none with a dark-mode partner.
 */
const statusLabels: Record<string, string> = {
  pending: 'Pending',
  verifying: 'Verifying',
  verified: 'Verified',
  failed: 'Failed',
}

/** Connection-type badge label (F8) — "smtp" renders as "SMTP", the OAuth
 * providers by name. */
const connectionTypeLabels: Record<string, string> = {
  smtp: 'SMTP',
  oauth_google: 'Google',
  oauth_microsoft: 'Microsoft',
}

const SenderEmailsPage: React.FC = () => {
  const {
    senderEmails, isLoading,
    fetchSenderEmails, deleteSenderEmail,
    verifySenderEmail, checkDns, setDefault,
  } = useSenderEmailStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingSender, setEditingSender] = useState<SenderEmail | null>(null)
  const [openMenu, setOpenMenu] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<SenderEmail | null>(null)
  const [verifyingId, setVerifyingId] = useState<number | null>(null)
  const [reconnectingId, setReconnectingId] = useState<number | null>(null)
  /** Wrapper of the row menu that is currently open (trigger + surface). */
  const menuRef = useRef<HTMLDivElement>(null)
  // Manager's "view" level on sender_emails.manage sees this page but can't
  // add/edit/delete/verify/check-DNS/set-default -- backend already 403s
  // these (routes/sender_emails.py checks `perm.scope == "view"` on every
  // mutation), this just keeps the UI from offering a control that would
  // only 403. Owner/Admin keep full write access ("manage").
  const { canWrite, scope: senderEmailsScope, allowed: senderEmailsAllowed } = usePermission('sender_emails', 'manage')
  // Adding a mailbox is for Owner/Admin only (AB#22516). Manager/Representative
  // hold "own": they keep managing the mailboxes assigned to them, but the Add
  // button is not offered. UI only, the backend rule is unchanged.
  const canAddMailbox = canWrite && senderEmailsScope !== 'own'
  const location = useLocation()
  const navigate = useNavigate()

  // Analyst holds no cell here at all (spec sect.4-D), so the list 403s --
  // don't fire it, and show why instead of an empty page (AB#22341).
  useEffect(() => {
    if (!senderEmailsAllowed) return
    fetchSenderEmails()
  }, [senderEmailsAllowed])

  // Nylas OAuth (F8) lands back here via a full-page redirect, not an API
  // response this component ever sees directly — `?connected=` /
  // `?oauth_error=` on the URL (routes/sender_emails.py's oauth_callback) is
  // the only signal of how it went. Without this, a declined consent screen
  // or a misconfigured provider app silently drops the user back on this
  // page with no feedback at all.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const connected = params.get('connected')
    const oauthError = params.get('oauth_error')
    if (!connected && !oauthError) return

    if (connected) {
      toast.success(`Connected ${connected}`)
      fetchSenderEmails()
    } else if (oauthError) {
      toast.error(`Could not connect the mailbox: ${oauthError}`)
    }
    navigate(location.pathname, { replace: true })

  }, [location.search])

  // Dismiss the row menu on an outside press or Escape. Without this the menu
  // stayed open until an item was clicked — including while another row's card
  // was interacted with.
  useEffect(() => {
    if (openMenu === null) return
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  const confirmDelete = () => {
    if (!pendingDelete) return
    const id = pendingDelete.id
    setPendingDelete(null)        // close dialog immediately — single clean transition
    void deleteSenderEmail(id)    // optimistic store removal handles the UI
  }

  const handleVerify = async (id: number) => {
    setVerifyingId(id)
    try {
      await verifySenderEmail(id)
    } finally {
      setVerifyingId(null)
      setOpenMenu(null)
    }
  }

  // F8: re-run the Nylas connect flow for a mailbox whose grant was revoked
  // (oauth_status === "grant_revoked"). Full-page redirect, same as the
  // initial connect in AddSenderEmailModal — the callback updates this same
  // row's grant_id in place (SenderEmailService.create_or_update_oauth_sender
  // is keyed on account_id + email_address).
  const handleReconnect = async (sender: SenderEmail) => {
    const provider = sender.connection_type === 'oauth_google' ? 'google' : 'microsoft'
    setReconnectingId(sender.id)
    try {
      const res = await senderEmailsApi.oauthAuthorize(provider)
      window.location.assign(res.data.authorize_url)
    } catch {
      setReconnectingId(null)
    }
  }

  // Both close the menu *before* awaiting: leaving it open for the duration of
  // the request let a second click fire the same POST twice.
  const handleCheckDns = async (id: number) => {
    setOpenMenu(null)
    await checkDns(id)
  }

  const handleSetDefault = async (id: number) => {
    setOpenMenu(null)
    await setDefault(id)
  }

  const stats = {
    total: senderEmails.length,
    verified: senderEmails.filter(s => s.status === 'verified').length,
    pending: senderEmails.filter(s => s.status === 'pending').length,
    failed: senderEmails.filter(s => s.status === 'failed').length,
  }

  if (!senderEmailsAllowed) {
    return (
      <SectionLocked title="Email accounts are managed by your account owner">
        Your role doesn&apos;t include access to the workspace&apos;s sender
        mailboxes or their SMTP configuration. Ask a workspace Owner or Admin
        if you need one set up.
      </SectionLocked>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-emphasis tracking-tight text-foreground">Email Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage sender email accounts for your campaigns
          </p>
        </div>
        {canAddMailbox && (
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Email Account
          </Button>
        )}
      </div>

      {!canWrite && (
        <ReadOnlyBanner>
          You can see the account's sender emails and their verification/DNS status,
          but only a workspace Owner or Admin can add, edit, delete, verify, or
          reconfigure them.
        </ReadOnlyBanner>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: Envelope },
          { label: 'Verified', value: stats.verified, icon: CheckCircle },
          { label: 'Pending', value: stats.pending, icon: Clock },
          { label: 'Failed', value: stats.failed, icon: WarningCircle },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <stat.icon className="h-4 w-4 text-muted-foreground/50" />
              </div>
              <p className="text-2xl font-emphasis text-foreground">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground dark:text-[#8a8f98]">
          <DotLoader size="lg" label="Loading sender emails" />
        </div>
      ) : senderEmails.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-14 w-14 rounded-xl bg-muted/70 flex items-center justify-center mb-4">
              <Envelope className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-emphasis tracking-tight text-foreground mb-2">No email accounts yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Add your first sender email to start sending campaigns.</p>
            {canAddMailbox && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Email Account
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {senderEmails.map((sender) => {
            const statusLabel = statusLabels[sender.status] || statusLabels.pending
            const iconTint = getBadgeIconTint(sender.status)
            return (
              <div key={sender.id}>
                {/* `interactive` for the hover tint only — the row itself is not
                    a link, so the variant's `cursor-pointer` is overridden. */}
                <Card variant="interactive" className="cursor-default">
                  <CardContent className="p-4">
                    {/* Wraps at narrow widths: the DNS chips (§ DnsStatusBadge)
                        are far wider than the three bare icons they replaced. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
                      <div className="flex items-center gap-4 flex-1 min-w-[15rem]">
                        <div className={cn('p-2.5 rounded-lg', iconTint.bg)}>
                          <Envelope className={cn('h-5 w-5', iconTint.fg)} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-emphasis text-foreground truncate">{sender.label}</h3>
                            {sender.is_default && (
                              <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1', badgeVariantStyles.info)}>
                                <Star className="h-3 w-3" /> Default
                              </span>
                            )}
                            <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium', getBadgeClasses(sender.status))}>
                              <span className={cn('h-1.5 w-1.5 rounded-full', getBadgeDotColor(sender.status))} />
                              {statusLabel}
                            </span>
                            {/* Provider badge (F8) — connection_type defaults
                                to "smtp" for every pre-existing sender. */}
                            <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', badgeVariantStyles.default)}>
                              {connectionTypeLabels[sender.connection_type] || 'SMTP'}
                            </span>
                            {sender.oauth_status === 'grant_revoked' && (
                              <>
                                <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', badgeVariantStyles.warning)}>
                                  Connection expired
                                </span>
                                {canWrite && (
                                  <button
                                    type="button"
                                    className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', badgeVariantStyles.warning, 'hover:opacity-80 transition-opacity')}
                                    disabled={reconnectingId === sender.id}
                                    aria-busy={reconnectingId === sender.id}
                                    onClick={() => handleReconnect(sender)}
                                  >
                                    {reconnectingId === sender.id ? 'Reconnecting…' : 'Reconnect'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{sender.email_address}</p>
                          <div className="flex items-center gap-3 mt-1">
                            {sender.display_name && (
                              <span className="text-xs text-muted-foreground">"{sender.display_name}"</span>
                            )}
                            {sender.connection_type === 'smtp' ? (
                              <span className="text-xs text-muted-foreground">
                                {sender.smtp_server}:{sender.smtp_port}
                              </span>
                            ) : sender.connection_type === 'oauth_google' ? (
                              <span className="text-xs text-muted-foreground">
                                Send only — reply detection not available for Gmail yet
                              </span>
                            ) : null}
                            <span className="text-xs text-muted-foreground">
                              {sender.emails_sent_today}/{sender.daily_send_limit} sent today
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* DNS records — shared badge: three states each (valid /
                          invalid / unknown), label always rendered, dual-mode. */}
                      <div className="flex-shrink-0">
                        <DnsStatusBadge
                          spf={sender.spf_valid}
                          dkim={sender.dkim_valid}
                          dmarc={sender.dmarc_valid}
                        />
                      </div>

                      {/* Actions */}
                      {canWrite && (
                      <div className="relative flex-shrink-0 ml-auto" ref={openMenu === sender.id ? menuRef : undefined}>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Actions for ${sender.label}`}
                          aria-haspopup="true"
                          aria-expanded={openMenu === sender.id}
                          onClick={() => setOpenMenu(openMenu === sender.id ? null : sender.id)}
                        >
                          <DotsThree className="h-4 w-4" />
                        </Button>
                        {openMenu === sender.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border/60 rounded-lg shadow-lg z-10 py-1 overflow-hidden">
                            {/* An OAuth-connected mailbox has nothing to
                                verify — the grant itself already proves
                                access (see SenderEmailService
                                .create_or_update_oauth_sender). */}
                            {sender.connection_type === 'smtp' && (
                              isFullyVerified(sender) ? (
                                /* A proven connection is a state, not an
                                   action. Gated on both flags rather than
                                   status -- status says 'verified' when only
                                   SMTP passed, and greying this out then would
                                   remove the only control that repairs IMAP. */
                                <div
                                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-emerald-700 dark:text-emerald-300"
                                  role="status"
                                >
                                  <ShieldCheck className="h-4 w-4" weight="fill" />
                                  Verified
                                </div>
                              ) : (
                                <button
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 flex items-center gap-2 transition-colors duration-100"
                                  onClick={() => handleVerify(sender.id)}
                                  disabled={verifyingId === sender.id}
                                  aria-busy={verifyingId === sender.id}
                                >
                                  {verifyingId === sender.id ? (
                                    // The button already announces via aria-busy — §55: don't double-speak.
                                    <DotLoader size="sm" announce={false} className="h-4 w-4" />
                                  ) : (
                                    <ArrowsClockwise className="h-4 w-4" />
                                  )}
                                  Verify Connection
                                </button>
                              )
                            )}
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 flex items-center gap-2 transition-colors duration-100"
                              onClick={() => handleCheckDns(sender.id)}
                            >
                              <Globe className="h-4 w-4" />
                              Check DNS
                            </button>
                            {!sender.is_default && sender.status === 'verified' && (
                              <button
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 flex items-center gap-2 transition-colors duration-100"
                                onClick={() => handleSetDefault(sender.id)}
                              >
                                <Star className="h-4 w-4" />
                                Set as Default
                              </button>
                            )}
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 flex items-center gap-2 transition-colors duration-100"
                              onClick={() => { setEditingSender(sender); setOpenMenu(null) }}
                            >
                              <PencilSimple className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 text-red-600 dark:text-red-400 flex items-center gap-2 transition-colors duration-100"
                              onClick={() => { setPendingDelete(sender); setOpenMenu(null) }}
                            >
                              <Trash className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                    {sender.verification_error && sender.status === 'failed' && (
                      <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-2 ml-14">{sender.verification_error}</p>
                    )}
                    <WarmupSection
                      senderEmailId={sender.id}
                      smtpVerified={sender.smtp_verified}
                      connectionType={sender.connection_type}
                    />
                  </CardContent>
                </Card>
              </div>
            )
          })}
        </div>
      )}

      {/* Modals. `AnimatePresence` sits *outside* each conditional on purpose:
          when it lived inside the modal component, flipping the condition false
          unmounted the AnimatePresence along with its child, so the exit variant
          never played and the modal vanished on the frame. The stable `key` is
          what lets presence track the child across that unmount. */}
      <AnimatePresence>
        {showAddModal && (
          <AddSenderEmailModal key="add-sender-email" onClose={() => setShowAddModal(false)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {editingSender && (
          <EditSenderEmailModal
            key="edit-sender-email"
            sender={editingSender}
            onClose={() => setEditingSender(null)}
          />
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete email account"
        description={
          <>Are you sure you want to delete <span className="font-medium text-foreground dark:text-[#f7f8f8]">{pendingDelete?.label || pendingDelete?.email_address}</span>? This action cannot be undone.</>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

export default SenderEmailsPage
